/**
 * Age Gate Component
 * Handles age verification pop-up with cookie persistence
 */

class AgeGate {
  constructor() {
    this.cookieName = 'age_verification_passed';
    this.cookieExpiry = 365; // 1 year in days
    this.modal = null;
    this.overlay = null;
    this.yesButton = null;
    this.noButton = null;
    
    this.init();
  }

  init() {
    // Don't show age gate in the theme editor
    if (window.Shopify && window.Shopify.designMode) {
      return;
    }

    // Check if user has already passed age verification
    if (this.getCookie(this.cookieName)) {
      return;
    }

    // Create and show the age gate
    this.createAgeGate();
    this.bindEvents();
    this.showAgeGate();
  }

  createAgeGate() {
    // Create overlay
    this.overlay = document.createElement('div');
    this.overlay.className = 'age-gate-overlay';
    this.overlay.setAttribute('aria-hidden', 'false');
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-labelledby', 'age-gate-question');
    this.overlay.setAttribute('aria-modal', 'true');

    // Create modal container
    this.modal = document.createElement('div');
    this.modal.className = 'age-gate-modal';

    // Get settings from CSS custom properties
    const settings = this.getSettings();

    // Apply custom styles
    this.modal.style.backgroundColor = settings.backgroundColor;
    this.modal.style.borderRadius = settings.borderRadius;

    // Create modal content
    const modalContent = `
      <div class="age-gate-content">
        ${settings.logo ? `<div class="age-gate-logo">
          <img src="${settings.logo}" alt="Logo" style="max-width: 250px; height: auto;">
        </div>` : ''}
        
        <div class="age-gate-question" id="age-gate-question" style="color: ${settings.textColor};">
          ${settings.questionText}
        </div>
        
        <div class="age-gate-buttons">
          <button type="button" class="age-gate-btn age-gate-yes button-secondary color-scheme-2" tabindex="0">
            Yes
          </button>
          <button type="button" class="age-gate-btn age-gate-no button-secondary color-scheme-2" tabindex="0">
            No
          </button>
        </div>
        
        ${settings.legalText ? `<div class="age-gate-legal" style="color: ${settings.legalTextColor};">
          ${settings.legalText}
        </div>` : ''}
      </div>
    `;

    this.modal.innerHTML = modalContent;
    this.overlay.appendChild(this.modal);

    // Get button references
    this.yesButton = this.modal.querySelector('.age-gate-yes');
    this.noButton = this.modal.querySelector('.age-gate-no');
  }

  getSettings() {
    const styles = getComputedStyle(document.documentElement);
    
    // Helper function to clean CSS custom property values
    const cleanValue = (value) => {
      if (!value) return '';
      return value.trim().replace(/^["']|["']$/g, ''); // Remove surrounding quotes
    };
    
    const backgroundColor = styles.getPropertyValue('--age-gate-bg-color').trim() || '#4AC0DB';
    const borderRadius = styles.getPropertyValue('--age-gate-border-radius').trim() || '0px';
    const textColor = styles.getPropertyValue('--age-gate-text-color').trim() || '#000000';
    const legalTextColor = styles.getPropertyValue('--age-gate-legal-text-color').trim() || '#666666';
    const questionText = cleanValue(styles.getPropertyValue('--age-gate-question-text')) || 'Are you over 21?';
    const legalText = cleanValue(styles.getPropertyValue('--age-gate-legal-text'));
    const redirectUrl = cleanValue(styles.getPropertyValue('--age-gate-redirect-url')) || 'https://www.google.com';
    const logoUrl = styles.getPropertyValue('--age-gate-logo-url').trim();
    
    let logo = '';
    if (logoUrl && logoUrl !== '""' && logoUrl !== "''") {
      // Extract URL from url() format
      const urlMatch = logoUrl.match(/url\(['"]?([^'"]*?)['"]?\)/);
      logo = urlMatch ? urlMatch[1] : logoUrl.replace(/^["']|["']$/g, '');
    }
    
    return {
      backgroundColor,
      borderRadius,
      textColor,
      legalTextColor,
      questionText,
      legalText,
      logo,
      redirectUrl
    };
  }

  bindEvents() {
    // Yes button - pass verification and close
    this.yesButton.addEventListener('click', () => this.handleYes());
    this.yesButton.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.handleYes();
      }
    });

    // No button - redirect to specified URL
    this.noButton.addEventListener('click', () => this.handleNo());
    this.noButton.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.handleNo();
      }
    });

    // Keyboard navigation
    this.overlay.addEventListener('keydown', (e) => this.handleKeydown(e));

    // Prevent closing by clicking overlay
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        e.preventDefault();
        e.stopPropagation();
      }
    });
  }

  handleYes() {
    this.setCookie(this.cookieName, 'true', this.cookieExpiry);
    this.hideAgeGate();
  }

  handleNo() {
    const settings = this.getSettings();
    let redirectUrl = settings.redirectUrl;
    
    // Fallback if no URL is set or if it's empty
    if (!redirectUrl || redirectUrl.trim() === '') {
      redirectUrl = 'https://www.google.com';
    }
    
    window.location.href = redirectUrl;
  }

  handleKeydown(e) {
    // Tab navigation between buttons
    if (e.key === 'Tab') {
      const focusableElements = [this.yesButton, this.noButton];
      const currentIndex = focusableElements.indexOf(document.activeElement);
      
      if (e.shiftKey) {
        // Shift + Tab (backwards)
        const prevIndex = currentIndex === 0 ? focusableElements.length - 1 : currentIndex - 1;
        focusableElements[prevIndex].focus();
      } else {
        // Tab (forwards)
        const nextIndex = currentIndex === focusableElements.length - 1 ? 0 : currentIndex + 1;
        focusableElements[nextIndex].focus();
      }
      e.preventDefault();
    }

    // Escape key - prevent closing
    if (e.key === 'Escape') {
      e.preventDefault();
    }
  }

  showAgeGate() {
    // Block scrolling on body
    document.body.style.overflow = 'hidden';
    
    // Add to DOM
    document.body.appendChild(this.overlay);
    
    // Focus the first button
    setTimeout(() => {
      this.yesButton.focus();
    }, 100);
  }

  hideAgeGate() {
    // Restore scrolling
    document.body.style.overflow = '';
    
    // Remove from DOM
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }
  }

  setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }

  getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }
}

// Initialize age gate when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new AgeGate());
} else {
  new AgeGate();
}
